
import React, { useState, useMemo } from 'react';
import './ShopPage.css';
import { useApp } from '../../context/AppContext';
import ProductCard from '../../components/ProductCard/ProductCard';

const ShopPage: React.FC = () => {
  const { products, currentUser } = useApp();
  const [activeCategory, setActiveCategory] = useState('all');
  const [sortBy, setSortBy] = useState('featured');
  const [showFilters, setShowFilters] = useState(false);
  const [priceRange, setPriceRange] = useState([0, 100]);

  const categories = ['all', 'Hoodies', 'Tops', 'Bottoms', 'Accessories'];

  const filteredProducts = useMemo(() => {
    let filtered = [...products];
    
    if (activeCategory !== 'all') {
      filtered = filtered.filter(p => p.category === activeCategory);
    }
    
    filtered = filtered.filter(p => {
      const price = p.salePrice || p.price;
      return price >= priceRange[0] && price <= priceRange[1];
    });

    switch (sortBy) {
      case 'price-low': filtered.sort((a, b) => (a.salePrice || a.price) - (b.salePrice || b.price)); break;
      case 'price-high': filtered.sort((a, b) => (b.salePrice || b.price) - (a.salePrice || a.price)); break;
      case 'newest': filtered.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime()); break;
      case 'popular': filtered.sort((a, b) => b.sold - a.sold); break;
      default: filtered.sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));
    }
    
    return filtered;
  }, [products, activeCategory, sortBy, priceRange]);

  const featuredProducts = products.filter(p => p.featured);
  const wishlistProducts = products.filter(p => currentUser.wishlist.includes(p.id));

  return (
    <div className="shop-page">
      <div className="shop-hero">
        <h1>Exclusive Collection</h1>
        <p>Limited edition fashion by Jalen Ward</p>
      </div>

      {featuredProducts.length > 0 && (
        <div className="featured-section">
          <h2 className="section-title">🔥 Featured Items</h2>
          <div className="featured-scroll">
            {featuredProducts.map(product => (
              <div key={product.id} className="featured-item">
                <ProductCard product={product} />
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="shop-controls">
        <div className="categories-scroll">
          {categories.map(cat => (
            <button
              key={cat}
              className={`category-btn ${activeCategory === cat ? 'active' : ''}`}
              onClick={() => setActiveCategory(cat)}
            >
              {cat === 'all' ? 'All Items' : cat}
            </button>
          ))}
        </div>
        
        <div className="sort-filter-row">
          <select className="sort-select" value={sortBy} onChange={e => setSortBy(e.target.value)}>
            <option value="featured">Featured</option>
            <option value="newest">Newest</option>
            <option value="popular">Popular</option>
            <option value="price-low">Price: Low to High</option>
            <option value="price-high">Price: High to Low</option>
          </select>
          <button className="filter-btn" onClick={() => setShowFilters(!showFilters)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="4" y1="21" x2="4" y2="14"/>
              <line x1="4" y1="10" x2="4" y2="3"/>
              <line x1="12" y1="21" x2="12" y2="12"/>
              <line x1="12" y1="8" x2="12" y2="3"/>
              <line x1="20" y1="21" x2="20" y2="16"/>
              <line x1="20" y1="12" x2="20" y2="3"/>
              <line x1="1" y1="14" x2="7" y2="14"/>
              <line x1="9" y1="8" x2="15" y2="8"/>
              <line x1="17" y1="16" x2="23" y2="16"/>
            </svg>
            Filters
          </button>
        </div>
      </div>

      {showFilters && (
        <div className="filters-panel animate-slide-up">
          <div className="filter-group">
            <label>Price Range</label>
            <div className="price-inputs">
              <input 
                type="number" 
                value={priceRange[0]} 
                onChange={e => setPriceRange([+e.target.value, priceRange[1]])}
                placeholder="Min"
              />
              <span>-</span>
              <input 
                type="number" 
                value={priceRange[1]} 
                onChange={e => setPriceRange([priceRange[0], +e.target.value])}
                placeholder="Max"
              />
            </div>
          </div>
        </div>
      )}

      {wishlistProducts.length > 0 && (
        <div className="wishlist-section">
          <h2 className="section-title">❤️ Your Wishlist ({wishlistProducts.length})</h2>
          <div className="wishlist-scroll">
            {wishlistProducts.map(product => (
              <div key={product.id} className="wishlist-item">
                <ProductCard product={product} />
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="products-section">
        <div className="section-header">
          <h2 className="section-title">All Products</h2>
          <span className="product-count">{filteredProducts.length} items</span>
        </div>
        <div className="products-grid">
          {filteredProducts.map(product => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default ShopPage;
